import { useState } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { FlipStorageProvider } from "../../../contexts/FlipStorageContext";
import PerformanceStats from "./PerformanceStats";
import ProfitCalculator from "./ProfitCalculator";
import FeeBreakdown from "./FeeBreakdown";
import FlipHistory from "./FlipHistory";

const SECTIONS = [
  { id: "performance", label: "Performance", icon: "ri-bar-chart-2-line" },
  { id: "calculator", label: "Profit Calc", icon: "ri-calculator-line" },
  { id: "fees", label: "Fee Breakdown", icon: "ri-percent-line" },
  { id: "history", label: "History", icon: "ri-history-line" },
] as const;

type SectionId = typeof SECTIONS[number]["id"];

export default function BetaDashboard() {
  const { isDark } = useTheme();
  const [activeSection, setActiveSection] = useState<SectionId>("performance");

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <FlipStorageProvider>
      <div>
        {/* Beta Banner */}
        <div className={`rounded-2xl p-4 border mb-6 flex items-center gap-3 ${isDark ? "bg-amber-500/6 border-amber-500/15" : "bg-amber-50 border-amber-200"}`}>
          <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${isDark ? "bg-amber-500/15" : "bg-amber-100"}`}>
            <i className={`ri-flask-line text-sm ${isDark ? "text-amber-400" : "text-amber-600"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-inter text-xs font-medium ${isDark ? "text-amber-400" : "text-amber-700"}`}>
              Beta Preview — Your data is saved locally in your browser. Real cloud sync activates at launch.
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            <p className={`font-inter text-[11px] ${isDark ? "text-amber-500/50" : "text-amber-400"}`}>{today}</p>
          </div>
        </div>

        {/* Inner Tab Nav */}
        <div className={`flex gap-1 rounded-xl p-1 mb-6 w-fit ${isDark ? "bg-white/4" : "bg-black/5"}`}>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-inter text-xs font-medium cursor-pointer transition-all duration-200 whitespace-nowrap ${
                activeSection === s.id
                  ? isDark ? "bg-white text-black" : "bg-white text-[#111] shadow-sm"
                  : isDark ? "text-[#555] hover:text-white" : "text-[#999] hover:text-[#555]"
              }`}
            >
              <i className={`${s.icon} text-[11px]`} />
              {s.label}
            </button>
          ))}
        </div>

        {/* Section Content */}
        {activeSection === "performance" && <PerformanceStats />}
        {activeSection === "calculator" && <ProfitCalculator />}
        {activeSection === "fees" && <FeeBreakdown />}
        {activeSection === "history" && <FlipHistory />}
      </div>
    </FlipStorageProvider>
  );
}
